import { barcodeLayout } from './barcodeLayout';
import { columnOriginX, mmToDots, verticalPitch } from './geometry';
import { getPrinter } from './printers';

const ORI = { 0: 'N', 90: 'R', 180: 'I', 270: 'B' };
const ALIGN = { left: 'L', center: 'C', right: 'R' };
const ROT = { 0: 0, 90: 1, 180: 2, 270: 3 };
const FONTS = [[1, 12], [2, 16], [3, 20], [4, 24], [5, 48]];

function pickFont(heightDots) {
  let best = { font: 1, mult: 1, err: Infinity };
  for (const [f, h] of FONTS) {
    for (let m = 1; m <= 9; m++) {
      const err = Math.abs(h * m - heightDots);
      if (err < best.err) best = { font: f, mult: m, err };
    }
  }
  return best;
}

const quote = (s) => `"${String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

function columnCount(model, opts) {
  if (opts.expandColumns) return model.roll.columns;
  return 1;
}

function transform(opts, text, colIndex) {
  let s = opts.transform ? opts.transform(text) : String(text ?? '');
  if (opts.expandColumns && colIndex > 0) {
    s = s.replace(/\{produto1\./g, `{produto${colIndex + 1}.`);
  }
  return s;
}

export function generateZpl(model, opts) {
  const { roll } = model;
  const d = (mm) => mmToDots(mm, opts.dpi);
  const cols = columnCount(model, opts);
  const out = ['^XA', '^CI28', `^PW${d(roll.rollWidth)}`, `^LL${d(roll.labelHeight)}`, '^LH0,0'];
  for (let c = 0; c < cols; c++) {
    const ox = columnOriginX(roll, c);
    for (const el of model.elements) {
      const x = Math.max(0, d(ox + el.x));
      const y = Math.max(0, d(roll.offsetY + el.y));
      const fo = `^FO${x},${y}`;
      const t = (s) => transform(opts, s, c);
      if (el.type === 'text') {
        const h = Math.max(10, d(el.fontSize));
        const bw = d(el.rotation % 180 === 0 ? el.w : el.h);
        out.push(`${fo}^A0${ORI[el.rotation] || 'N'},${h},${h}^FB${bw},${Math.max(1, el.maxLines)},0,${ALIGN[el.align] || 'L'},0^FD${t(el.content)}^FS`);
      } else if (el.type === 'barcode') {
        const layout = barcodeLayout(el);
        const custom = el.textFontSize !== undefined;
        const h = d(custom ? layout.barHeight : layout.height);
        const hr = el.showText && !custom ? 'Y' : 'N';
        const cmd = el.symbology === 'EAN13'
          ? `^BE${ORI[el.rotation] || 'N'},${h},${hr},N`
          : `^BC${ORI[el.rotation] || 'N'},${h},${hr},N,N`;
        out.push(`${fo}^BY${el.moduleWidth},2,${h}${cmd}^FD${t(el.content)}^FS`);
        if (custom && el.showText) {
          const font = Math.max(1, d(layout.fontSize));
          const [dx, dy] = layout.captionOffset;
          out.push(`^FO${Math.max(0, x + d(dx))},${Math.max(0, y + d(dy))}^A0${ORI[el.rotation] || 'N'},${font},${font}^FB${d(layout.width)},1,0,C,0^FD${t(el.content)}^FS`);
        }
      } else if (el.type === 'line') {
        const th = Math.max(1, d(el.thickness));
        out.push(el.orientation === 'h' ? `${fo}^GB${d(el.w)},${th},${th}^FS` : `${fo}^GB${th},${d(el.h)},${th}^FS`);
      } else if (el.type === 'rect') {
        const th = Math.max(1, d(el.thickness));
        out.push(`${fo}^GB${d(el.w)},${d(el.h)},${th}^FS`);
      }
    }
  }
  out.push('^XZ');
  return out.join('\n');
}

export function generateEpl(model, opts) {
  const { roll } = model;
  const d = (mm) => mmToDots(mm, opts.dpi);
  const cols = columnCount(model, opts);
  const out = ['', 'N', `q${d(roll.rollWidth)}`, `Q${d(roll.labelHeight)},${d(verticalPitch(roll) - roll.labelHeight)}`];
  for (let c = 0; c < cols; c++) {
    const ox = columnOriginX(roll, c);
    for (const el of model.elements) {
      const x = Math.max(0, d(ox + el.x));
      const y = Math.max(0, d(roll.offsetY + el.y));
      const t = (s) => transform(opts, s, c);
      if (el.type === 'text') {
        const f = pickFont(d(el.fontSize));
        out.push(`A${x},${y},${ROT[el.rotation] || 0},${f.font},${f.mult},${f.mult},N,${quote(t(el.content))}`);
      } else if (el.type === 'barcode') {
        const layout = barcodeLayout(el);
        const custom = el.textFontSize !== undefined;
        const h = d(custom ? layout.barHeight : layout.height);
        const sym = el.symbology === 'EAN13' ? 'E30' : '1';
        out.push(`B${x},${y},${ROT[el.rotation] || 0},${sym},${el.moduleWidth},${el.moduleWidth * 2},${h},${el.showText && !custom ? 'B' : 'N'},${quote(t(el.content))}`);
        if (custom && el.showText) {
          const f = pickFont(d(layout.fontSize));
          const [dx, dy] = layout.captionOffset;
          out.push(`A${Math.max(0, x + d(dx))},${Math.max(0, y + d(dy))},${ROT[el.rotation] || 0},${f.font},${f.mult},${f.mult},N,${quote(t(el.content))}`);
        }
      } else if (el.type === 'line') {
        const th = Math.max(1, d(el.thickness));
        out.push(el.orientation === 'h' ? `LO${x},${y},${d(el.w)},${th}` : `LO${x},${y},${th},${d(el.h)}`);
      } else if (el.type === 'rect') {
        const th = Math.max(1, d(el.thickness));
        out.push(`X${x},${y},${th},${x + d(el.w)},${y + d(el.h)}`);
      }
    }
  }
  out.push('P1');
  return out.join('\n');
}

export function generate(lang, model, opts) {
  if (lang === 'ZPL') return generateZpl(model, opts);
  if (lang === 'EPL' || lang === 'PPLB') return generateEpl(model, opts);
  return null;
}

export function gerarTemplate(model) {
  const printer = getPrinter(model.printerId);
  if (!printer.erpLanguage) return '';
  const code = generate(printer.erpLanguage, model, { dpi: printer.dpi, expandColumns: true }) || '';
  const tamanho = String(model.erp?.tamanhoDescricao || '').trim();
  const cab = /^\d+$/.test(tamanho) ? `TamanhoDescricao=${tamanho}\n` : '';
  return `${cab}${code}`;
}
